import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Button,
    Chip,
    CircularProgress,
    TextField,
    Paper
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { liveURL, IMAGE_URL } from '../constants/Consts';

const MyPosts = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [message, setMessage] = useState('');
    const { authToken } = useAuth();

    const fetchMyPosts = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${liveURL}/v1/adminPosts/getMyPosts`, {
                headers: { token: authToken }
            });
            if (response.data.Result) {
                console.log("response.data.data",response.data);
                setPosts(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching posts:', error);
        } finally {
            setLoading(false);
        }
    }, [authToken]);

    useEffect(() => {
        if (authToken) fetchMyPosts();
    }, [authToken, fetchMyPosts]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append('image', selectedFile);
        formData.append('title', title);
        formData.append('description', description);

        try {
            setUploading(true);
            const response = await axios.post(`${liveURL}/v1/adminPosts/upload`, formData, {
                headers: { 
                    token: authToken,
                    'Content-Type': 'multipart/form-data'
                }
            });
            if (response.data.Result) {
                setMessage('Post uploaded successfully and is pending approval.');
                setSelectedFile(null);
                setPreviewUrl(null);
                setTitle('');
                setDescription('');
                fetchMyPosts();
            }
        } catch (error) {
            console.error('Upload error:', error);
            setMessage('Upload failed.');
        } finally {
            setUploading(false);
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'approved': return <Chip label="Approved" color="success" size="small" />;
            case 'rejected': return <Chip label="Rejected" color="error" size="small" />;
            default: return <Chip label="Pending" color="warning" size="small" />;
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                My Posts
            </Typography>

            <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>Upload New Post</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, maxWidth: 600, mx: 'auto' }}>
                    {previewUrl && (
                        <Box sx={{ width: '100%', maxWidth: 400, borderRadius: 2, overflow: 'hidden', boxShadow: 2 }}>
                            <img src={previewUrl} alt="Preview" style={{ width: '100%', display: 'block' }} />
                        </Box>
                    )}
                    
                    <TextField
                        label="Post Title"
                        variant="outlined"
                        fullWidth
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Enter post title..."
                    />

                    <TextField
                        label="Post Description"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Enter post description..."
                    />

                    <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="raised-button-file"
                        type="file"
                        onChange={handleFileChange}
                    />
                    <label htmlFor="raised-button-file">
                        <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />}>
                            {selectedFile ? selectedFile.name : 'Select Image'}
                        </Button>
                    </label>
                    <Button 
                        variant="contained" 
                        onClick={handleUpload} 
                        disabled={!selectedFile || uploading}
                        sx={{ minWidth: 200 }}
                    >
                        {uploading ? <CircularProgress size={24} color="inherit" /> : 'Upload Post'}
                    </Button>
                    {message && <Typography color="primary" variant="body2">{message}</Typography>}
                </Box>
            </Paper>

            <Typography variant="h6" gutterBottom>Your Uploads</Typography>
            {loading ? (
                <Box sx={{ textAlign: 'center', mt: 4 }}><CircularProgress /></Box>
            ) : (
                <Grid container spacing={3}>
                    {posts.map((post) => (
                        <Grid item xs={12} sm={6} md={4} key={post.id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: 3 }}>
                                <CardMedia
                                    component="img"
                                    height="200"
                                    image={`${IMAGE_URL}${post.image_url}`}
                                    alt="post"
                                    sx={{ objectFit: 'cover' }}
                                />
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        {getStatusChip(post.status)}
                                        <Typography variant="caption" color="textSecondary">
                                            {new Date(post.created_at).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                    {post.title && (
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 1 }}>
                                            {post.title}
                                        </Typography>
                                    )}
                                    {post.description && (
                                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                            {post.description}
                                        </Typography>
                                    )}
                                    {post.admin_comment && (
                                        <Box sx={{ mt: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Admin Comment:</Typography>
                                            <Typography variant="body2">{post.admin_comment}</Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {posts.length === 0 && (
                        <Grid item xs={12}>
                            <Typography align="center" color="textSecondary">No posts uploaded yet.</Typography>
                        </Grid>
                    )}
                </Grid>
            )}
        </Box>
    );
};

export default MyPosts;
